import { getParent, types, type Instance } from 'mobx-state-tree';
import AttributesInspector from './attributes_inspector';
import MultiplePropagationConstraintInspector from './multiple_propagation_constraint_inspector';
import type { IInspectorParent } from './inspector_parent';

/** The change keys stored by the object inspector. */
type ObjectChangeKey = 'name' | 'description' | 'constraints' | 'abstract';
/** A change value is a string (name/description/constraints) or boolean (abstract). */
type ObjectChangeValue = string | boolean | null;

/**
 * Store the changes in the object inspector that are currently pending.
 */
const ObjectInspectorChanges = types
  .model('ObjectInspectorChanges', {
    name: types.optional(types.maybeNull(types.string), null),
    description: types.optional(types.maybeNull(types.string), null),
    constraints: types.optional(types.maybeNull(types.string), null),
    abstract: types.optional(types.maybeNull(types.boolean), null),
  })
  .views((self) => ({
    // the inspector is dirty if any of the attributes has changes
    get dirty() {
      return (
        self.name !== null ||
        self.description !== null ||
        self.constraints !== null ||
        self.abstract !== null
      );
    },
  }))
  .actions((self) => ({
    /**
     * Store a change for the given key.
     *
     * This action also receives the original value of the attribute and clears
     * the stored change if the new value for the change is the same as the
     * original value.
     */
    storeChange(key: ObjectChangeKey, original: ObjectChangeValue, change: ObjectChangeValue) {
      // Dynamic indexed write across keys of differing value types (string vs
      // boolean); view the change props through a uniform record so the
      // assignment types. MST narrows at runtime — type-only, byte-identical.
      const writable = self as unknown as Record<ObjectChangeKey, ObjectChangeValue>;
      if (original !== change) {
        writable[key] = change;
      } else {
        writable[key] = null;
      }
    },
    discard() {
      self.name = null;
      self.description = null;
      self.constraints = null;
      self.abstract = null;
    },
  }));

/**
 * UI state for the object inspector
 */
const ObjectInspector = types
  .model('ObjectInspector', {
    // the pending changes
    changes: types.optional(ObjectInspectorChanges, () => ObjectInspectorChanges.create({})),
    // the state for the nested attributes inspector, see
    // `attributes_inspector.js`
    attributesInspector: types.optional(AttributesInspector, () => AttributesInspector.create({})),
    // the state for the nested multiple propagation constraint inspector, see
    // `multiple_propagation_constraint_inspector.js`
    mpcInspector: types.optional(MultiplePropagationConstraintInspector, () =>
      MultiplePropagationConstraintInspector.create({})
    ),
  })
  .views((self) => ({
    // We are dirty when we have changes ourself or the nested attributes
    // inspector is dirty
    get dirty() {
      return self.changes.dirty || self.attributesInspector.dirty || self.mpcInspector.dirty;
    },
    get selectedMPC() {
      return self.mpcInspector.selected;
    },
    get isEditingMPC() {
      return self.mpcInspector.editing != null;
    },
    get addedMPCs() {
      return self.mpcInspector.changes.added;
    },
  }))
  .actions((self) => ({
    // Discard our own changes and those of the nested attributes inspector
    discardChanges() {
      self.changes.discard();
      self.attributesInspector.discardChanges();
      self.mpcInspector.discardChanges();
    },
    clearSelections() {
      self.mpcInspector.clearSelections();
    },
    setProjectBusy(busy: boolean) {
      getParent<IInspectorParent>(self).setProjectBusy(busy);
    },
  }));

export type IObjectInspector = Instance<typeof ObjectInspector>;

export default ObjectInspector;
